import React, { useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import type { SpaceType } from '@/pages/Settings/UserManagement/SpaceAssignments';

interface SpaceAccessGuardProps {
  children: React.ReactNode;
}

/**
 * SpaceAccessGuard - Checks URL parameters for space IDs and validates user access.
 * If the user doesn't have access to the space in the URL, redirects to home.
 * If user has no access at all, still renders the dashboard (with empty sidebar).
 */
export const SpaceAccessGuard: React.FC<SpaceAccessGuardProps> = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const { hasAccess, isLoading, isUnrestricted } = useSpaceAccess();

  // Extract space IDs from URL parameters
  const urlSpaces = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const spaces: { type: SpaceType; id: string; paramName: string }[] = [];

    // Check each URL parameter for space IDs
    const spaceId = params.get('spaceId'); // Floor ID (format: space_xxx or floor_xxx)
    const roomId = params.get('roomId');
    const zoneId = params.get('zoneId');
    const buildingId = params.get('buildingId');

    if (spaceId) {
      spaces.push({ type: 'floor', id: spaceId, paramName: 'spaceId' });
    }
    if (roomId) {
      spaces.push({ type: 'room', id: roomId, paramName: 'roomId' });
    }
    if (zoneId) {
      spaces.push({ type: 'zone', id: zoneId, paramName: 'zoneId' });
    }
    if (buildingId) {
      spaces.push({ type: 'building', id: buildingId, paramName: 'buildingId' });
    }

    return spaces;
  }, [location.search]);

  // Check if user has access to all spaces in the URL
  const blockedSpace = useMemo(() => {
    // Still loading - don't block yet
    if (isLoading) {
      return null;
    }

    // User is admin/superuser - allow all
    if (isUnrestricted) {
      return null;
    }

    // No spaces in URL - nothing to block
    if (urlSpaces.length === 0) {
      return null;
    }

    // Check each space in the URL
    for (const space of urlSpaces) {
      const allowed = hasAccess(space.type, space.id);
      if (!allowed) {
        return space;
      }
    }

    return null;
  }, [isLoading, isUnrestricted, urlSpaces, hasAccess]);

  // Redirect to home if user doesn't have access to a space in the URL
  useEffect(() => {
    if (blockedSpace && !isLoading) {
      history.push('/');
    }
  }, [blockedSpace, isLoading, history]);

  // Don't render children until access is verified to prevent flash of protected content
  if (isLoading) {
    return null;
  }

  if (blockedSpace) {
    return null;
  }

  return <>{children}</>;
};

export default SpaceAccessGuard;
